import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone -> code -> done
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (raw.startsWith("+")) return raw.replace(/[^\d+]/g, "");
    return `+${digits}`;
  };

  const startCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!phone.trim()) { setError("Phone number is required"); return; }

    const formatted = formatPhone(phone);
    if (formatted.length < 11) { setError("Please enter a valid phone number"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { phone: formatted });
      setStep("code");
      startCooldown();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to send reset code.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (code.length !== 6) { setError("Please enter the 6-digit code"); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }

    const formatted = formatPhone(phone);
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        phone: formatted,
        code,
        new_password: newPassword,
      });
      setStep("done");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      <div className="flex flex-col justify-center w-full max-w-md px-8 py-12 bg-white">
        <div className="mb-10">
          <img src="/logo.svg" alt="Relay" className="h-28 w-auto" />
        </div>

        {step === "phone" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold font-heading mb-1.5" style={{ color: "#0F1419" }}>Reset password</h1>
              <p className="text-slate-500 text-sm">Enter the phone number associated with your account.</p>
            </div>

            <form onSubmit={handleSendCode} className="space-y-4">
              {error && <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="input"
                  placeholder="(702) 555-0123"
                  required
                  autoFocus
                />
                <p className="text-xs text-slate-400 mt-1">We'll send a 6-digit reset code to this number.</p>
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Sending code...</span>
                ) : (
                  <span className="flex items-center gap-2">Send Reset Code <ArrowRight size={16} /></span>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              <Link to="/login" className="font-medium hover:underline flex items-center justify-center gap-1" style={{ color: "#FF5C35" }}>
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </p>
          </>
        )}

        {step === "code" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold font-heading mb-1.5" style={{ color: "#0F1419" }}>Enter code & new password</h1>
              <p className="text-slate-500 text-sm">
                We sent a 6-digit code to <strong className="text-slate-700">{phone}</strong>
              </p>
            </div>

            <form onSubmit={handleReset} className="space-y-4">
              {error && <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Verification Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="input text-center text-2xl font-mono tracking-[0.3em]"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="input"
                  placeholder="Min. 6 characters"
                />
              </div>

              <button type="submit" disabled={loading || code.length !== 6 || newPassword.length < 6} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Resetting...</span>
                ) : "Reset Password"}
              </button>

              <div className="flex items-center justify-between pt-2">
                <button type="button" onClick={() => { setStep("phone"); setError(""); setCode(""); setNewPassword(""); }} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  <ArrowLeft size={14} /> Back
                </button>
                <button type="button" onClick={handleSendCode} disabled={resendCooldown > 0} className="text-sm font-medium disabled:text-slate-400" style={resendCooldown > 0 ? {} : { color: "#FF5C35" }}>
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          </>
        )}

        {step === "done" && (
          <>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#FFF0EB" }}>
                <CheckCircle2 size={28} style={{ color: "#FF5C35" }} />
              </div>
              <h1 className="text-3xl font-semibold font-heading mb-1.5" style={{ color: "#0F1419" }}>Password reset!</h1>
              <p className="text-slate-500 text-sm">You can now sign in with your new password.</p>
            </div>

            <button onClick={() => navigate("/login")} className="btn-primary w-full justify-center py-2.5">
              <span className="flex items-center gap-2">Sign in <ArrowRight size={16} /></span>
            </button>
          </>
        )}
      </div>

      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1756435292384-1bf32eff7baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwaG91c2V8ZW58MHx8fHwxNzc1MzM5MTM0fDA&ixlib=rb-4.1.0&q=85" alt="Modern real estate" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom right, rgba(15,20,25,0.75), rgba(255,92,53,0.25))" }} />
      </div>
    </div>
  );
}
